/**
 * Persistence layer.
 *
 * Two responsibilities:
 *
 * 1. Mirror the `state` signal to localStorage as an offline cache.
 *    Reads use the cache as a stale-but-fast fallback; writes always
 *    update the cache synchronously so a tab that crashes mid-edit
 *    doesn't lose the user's input.
 *
 * 2. Push diffs to Supabase. Every state mutation triggers a check:
 *    if no push is in flight, compute the diff against the
 *    `lastSyncedSnapshot` and send the upserts/deletes; if a push
 *    IS in flight, set `pushAgain = true` so the in-flight push
 *    re-runs once it finishes.
 *
 * Race condition prevention:
 *   The snapshot baseline is taken at the START of the push (before
 *   any await). Mutations during the round-trip register as a fresh
 *   diff for the next push. See ARCHITECTURE.md for the full story.
 *
 * Demo mode:
 *   `lastSyncedSnapshot` stays null → `saveStore` writes only to
 *   localStorage and never touches Supabase.
 */
import { effect } from "@preact/signals";
import { state, isDemoMode } from "./store";
import { LS_STATE_KEY } from "../config";
import { toast } from "../utils/toast";
import { restUpsert, restDelete } from "../utils/withRetry";
import type {
  AppState,
  RosterEntry,
  RosterMap,
  CalendarMap,
  NoteMap,
  MeetingMap,
} from "../types";

export interface Snapshot {
  roster: RosterMap;
  calendar: CalendarMap;
  notes: NoteMap;
  meetings: MeetingMap;
}

let lastSyncedSnapshot: Snapshot | null = null;
let pushInFlight = false;
let pushAgain = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** Exponential-backoff scaffold for retry-after-failure.
 *  Tracks consecutive failures so the backoff resets the moment a push
 *  finally lands. Schedule (in seconds): 5, 15, 30, 60, then steady at
 *  60. We never give up — a transient outage can last minutes; capping
 *  at 60s means we recover quickly when the upstream comes back without
 *  hammering it during the outage.
 *
 *  Toast suppression: with 122 users all hitting an outage and a
 *  failure every ~60s, the toast would re-fire every cycle. We squelch
 *  to one toast per 30 seconds. */
let consecutiveFailures = 0;
let lastErrorToastAt = 0;
const BACKOFF_SCHEDULE_S = [5, 15, 30, 60];
const ERROR_TOAST_DEDUP_MS = 30_000;

/** Set to `true` while a realtime event is being applied to state. The
 *  saveStore effect checks this and skips both the localStorage write
 *  AND the push trigger — the baseline is updated by markCellSynced /
 *  markRosterSynced (called from realtime.ts BEFORE the state mutation),
 *  so there's nothing for pushOnce to push back.
 *
 *  Without this guard, every realtime broadcast from another user's
 *  edit fired a phantom diff in this tab — for a regular employee that
 *  diff hit `is_my_roster(other-person) = false` in RLS and rejected,
 *  surfacing as a "Couldn't save your edits" toast even though the
 *  user's own edits were fine. The guard kills the cascade. */
let isApplyingRealtime = false;
export function setApplyingRealtime(v: boolean): void {
  isApplyingRealtime = v;
}

/** Per-row baseline writer for calendar / notes / meetings.
 *  Called by realtime handlers (before they mutate state) AND by
 *  pushOnce after a successful upsert/delete. Keeping the baseline
 *  current per-row means: (a) realtime echoes don't show up as a
 *  diff in the next push, and (b) a successful own-push doesn't
 *  clobber any realtime updates that arrived during the round-trip. */
export function markCellSynced(
  table: "calendar" | "notes" | "meetings",
  person: string,
  day: string,
  value: string | null,
): void {
  if (!lastSyncedSnapshot) return;
  const map = lastSyncedSnapshot[table] as Record<string, Record<string, string>>;
  if (value === null) {
    if (map[person]) {
      delete map[person][day];
      if (Object.keys(map[person]).length === 0) delete map[person];
    }
  } else {
    map[person] = map[person] ?? {};
    map[person][day] = value;
  }
}

/** Per-row baseline writer for roster.
 *  Pass `null` for the entry to drop the row entirely (DELETE event). */
export function markRosterSynced(name: string, entry: RosterEntry | null): void {
  if (!lastSyncedSnapshot) return;
  if (entry === null) {
    delete lastSyncedSnapshot.roster[name];
  } else {
    lastSyncedSnapshot.roster[name] = entry;
  }
}

/** Read the synced-baseline cell value. Used by realtime handlers to
 *  detect "stale echo" races — when the broadcast is the echo of our
 *  own already-superseded write, the realtime handler must not clobber
 *  the local pending edit. Returns null if there's no baseline yet
 *  (pre-boot) or no entry for the (person, day) pair. */
export function readBaselineCell(
  table: "calendar" | "notes" | "meetings",
  person: string,
  day: string,
): string | null {
  if (!lastSyncedSnapshot) return null;
  const map = lastSyncedSnapshot[table] as Record<string, Record<string, string>>;
  return map[person]?.[day] ?? null;
}

/** Roster counterpart to readBaselineCell. Returns the synced roster
 *  entry (or null if not yet synced). The race we guard against here
 *  is two admins editing the same row in quick succession — without
 *  the check, the slower admin's edit can be overwritten by the
 *  realtime echo of the faster one before their second push fires. */
export function readBaselineRoster(name: string): RosterEntry | null {
  if (!lastSyncedSnapshot) return null;
  return lastSyncedSnapshot.roster[name] ?? null;
}

/** Deep-clone the data slices we diff. JSON round-trip is fine here —
 *  state is plain JSON-shaped (no Date / Map / Set values). */
function takeSnapshot(s: AppState): Snapshot {
  return {
    roster: JSON.parse(JSON.stringify(s.roster)),
    calendar: JSON.parse(JSON.stringify(s.calendar)),
    notes: JSON.parse(JSON.stringify(s.notes)),
    meetings: JSON.parse(JSON.stringify(s.meetings)),
  };
}

/** Mark the current state as already-synced. Call this once after a
 *  successful boot from Supabase and again after the demo mode loads
 *  synthetic data (well — for demo mode we leave it null on purpose
 *  so saveStore never tries to push). */
export function recordSyncBaseline(snapshot?: Snapshot): void {
  lastSyncedSnapshot = snapshot ?? takeSnapshot(state.value);
}

export function clearSyncBaseline(): void {
  lastSyncedSnapshot = null;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  consecutiveFailures = 0;
  lastErrorToastAt = 0;
}

export interface DiffOp {
  table: "calendar" | "notes" | "meetings" | "roster";
  upserts: Array<Record<string, unknown>>;
  deletes: Array<{ person: string; day: string }>;
}

/** Exported (alongside the Snapshot type) so the unit tests in
 *  `storage.test.ts` can lock in the diff semantics for the post-
 *  realtime-fix architecture. Not used outside the module otherwise. */
export function computeDiffs(prev: Snapshot, next: Snapshot): DiffOp[] {
  const ops: DiffOp[] = [];

  for (const table of ["calendar", "notes", "meetings"] as const) {
    const upserts: Array<Record<string, unknown>> = [];
    const deletes: Array<{ person: string; day: string }> = [];
    const prevMap = prev[table];
    const nextMap = next[table];
    const persons = new Set([...Object.keys(prevMap), ...Object.keys(nextMap)]);
    persons.forEach((person) => {
      const prevDays = prevMap[person] ?? {};
      const nextDays = nextMap[person] ?? {};
      const days = new Set([...Object.keys(prevDays), ...Object.keys(nextDays)]);
      days.forEach((day) => {
        const pv = (prevDays as Record<string, unknown>)[day];
        const nv = (nextDays as Record<string, unknown>)[day];
        if (pv === nv) return;
        if (nv == null) {
          deletes.push({ person, day });
        } else if (table === "calendar") {
          upserts.push({ person, day, status: nv });
        } else if (table === "notes") {
          upserts.push({ person, day, text: String(nv) });
        } else {
          // meetings: nv is the description (may be empty string)
          upserts.push({ person, day, description: String(nv) });
        }
      });
    });
    if (upserts.length || deletes.length) ops.push({ table, upserts, deletes });
  }

  // Roster: send any row whose mutable fields changed (admin only — RLS
  // gates writes server-side anyway). Used for the Directory's edit flow.
  const rosterUpserts: Array<Record<string, unknown>> = [];
  const prevR = prev.roster;
  const nextR = next.roster;
  Object.keys(nextR).forEach((name) => {
    const a = prevR[name] ?? { unit: "", role: "", email: "" };
    const b = nextR[name];
    if (!b) return;
    if (a.unit !== b.unit || a.role !== b.role || a.email !== b.email) {
      rosterUpserts.push({
        name,
        unit: b.unit,
        role: b.role,
        email: b.email,
      });
    }
  });
  if (rosterUpserts.length) ops.push({ table: "roster", upserts: rosterUpserts, deletes: [] });

  return ops;
}

async function pushOnce(): Promise<void> {
  if (lastSyncedSnapshot === null || isDemoMode.value) return;
  pushInFlight = true;
  // Capture the snapshot we're about to send BEFORE the network round-trip.
  // Mutations between now and the await resolution must register as a new
  // diff for the next push, so the new baseline must be sentSnapshot, not
  // the live state object. (See the race-condition discussion in
  // ARCHITECTURE.md.)
  const sentSnapshot = takeSnapshot(state.value);
  try {
    const ops = computeDiffs(lastSyncedSnapshot, sentSnapshot);
    for (const op of ops) {
      if (op.upserts.length) {
        for (let i = 0; i < op.upserts.length; i += 500) {
          const chunk = op.upserts.slice(i, i + 500);
          // restUpsert bypasses the Supabase JS SDK's auth-token lock,
          // which the realtime channel ALSO holds. Under heavy realtime
          // traffic (100+ active users at launch) the SDK's lock queue
          // backs up enough to make pushOnce calls hang 5–30s. Raw fetch
          // reads the JWT straight from localStorage and ships it as a
          // Bearer token, so there's no lock to contest.
          await restUpsert(op.table, chunk);
          // Mark each upserted row as synced INDIVIDUALLY (not via a
          // wholesale `lastSyncedSnapshot = sentSnapshot` assignment).
          // Wholesale assignment used to clobber any realtime updates
          // that arrived during the round-trip; per-row keeps both
          // streams consistent.
          for (const item of chunk) {
            if (op.table === "roster") {
              const name = item.name as string;
              const entry = sentSnapshot.roster[name] ?? null;
              markRosterSynced(name, entry);
            } else {
              const person = item.person as string;
              const day = item.day as string;
              const value =
                op.table === "calendar"
                  ? (item.status as string)
                  : op.table === "notes"
                    ? (item.text as string) ?? ""
                    : (item.description as string) ?? "";
              markCellSynced(op.table, person, day, value);
            }
          }
        }
      }
      if (op.deletes.length) {
        // Group deletes by person → one round-trip per affected person
        // instead of one per (person, day) pair. Big win for "clear
        // whole month" type operations.
        const byPerson: Record<string, string[]> = {};
        op.deletes.forEach(({ person, day }) => {
          (byPerson[person] = byPerson[person] ?? []).push(day);
        });
        for (const person of Object.keys(byPerson)) {
          const days = byPerson[person] ?? [];
          for (let i = 0; i < days.length; i += 200) {
            const slice = days.slice(i, i + 200);
            const filter =
              `person=eq.${encodeURIComponent(person)}` +
              `&day=in.(${slice.map((d) => encodeURIComponent(d)).join(",")})`;
            await restDelete(op.table, filter);
            // Per-row baseline update (same rationale as upsert above).
            for (const day of slice) {
              if (op.table === "roster") continue; // roster deletes not supported in this path
              markCellSynced(
                op.table as "calendar" | "notes" | "meetings",
                person,
                day,
                null,
              );
            }
          }
        }
      }
    }
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    // Successful push → reset the failure streak so the next failure
    // starts at the bottom of the backoff schedule.
    consecutiveFailures = 0;
  } catch (err) {
    // Raw-fetch path already raced AbortController + did the local
    // expiry check; anything thrown here is a real failure — network
    // out, 4xx (RLS), 5xx, or "Session expired" pending background
    // refresh. Schedule a retry with exponential backoff and dedupe
    // the toast so the user sees one message, not one per cycle.
    const msg = err instanceof Error ? err.message : String(err ?? "");
    console.error("Supabase push failed:", err);
    consecutiveFailures++;
    const now = Date.now();
    if (now - lastErrorToastAt > ERROR_TOAST_DEDUP_MS) {
      toast(`Couldn't save your edits: ${msg}. Retrying…`);
      lastErrorToastAt = now;
    }
    const backoffSec =
      BACKOFF_SCHEDULE_S[Math.min(consecutiveFailures - 1, BACKOFF_SCHEDULE_S.length - 1)] ?? 60;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (!pushInFlight && lastSyncedSnapshot) void pushOnce();
    }, backoffSec * 1000);
  } finally {
    pushInFlight = false;
    if (pushAgain) {
      pushAgain = false;
      void pushOnce();
    }
  }
}

/** Fire-and-forget. Mirrors state to localStorage and triggers a push if
 *  one isn't already in flight.
 *
 *  When a realtime event triggered the state change, both side-effects
 *  are skipped: the localStorage write (because the JSON.stringify of a
 *  37k-row state is ~50ms — too expensive to run on every broadcast
 *  from another user) and the push trigger (because the realtime
 *  handler already updated lastSyncedSnapshot, so the next diff would
 *  be empty anyway and would just waste a round-trip + auth lock). */
export function saveStore(): void {
  if (isApplyingRealtime) return;
  try {
    localStorage.setItem(LS_STATE_KEY, JSON.stringify(state.value));
  } catch {
    /* quota / private mode — ignore */
  }
  if (lastSyncedSnapshot === null || isDemoMode.value) return;
  if (pushInFlight) {
    pushAgain = true;
    return;
  }
  void pushOnce();
}

/** Wire the signal: any state change triggers a saveStore. Called once
 *  from main.tsx. The first call to saveStore after a state replacement
 *  is debounced inside pushOnce via the inFlight + pushAgain flags. */
export function attachAutoSave(): void {
  let first = true;
  effect(() => {
    // Read the signal so the effect subscribes to it
    void state.value;
    if (first) {
      first = false;
      return;
    }
    saveStore();
  });
}

/** Read the cached state from localStorage (used as a stale-but-fast
 *  initial render until bootFromSupabase() arrives).
 *
 *  Merge the parsed cache with safe defaults BEFORE returning. The cache
 *  schema evolves over time (e.g. `closures` was added in a later
 *  migration than `calendar/notes/meetings`), and a user whose cache was
 *  written before a field existed will return `undefined` for it. That
 *  used to crash components doing `Object.entries(s.closures)` etc. By
 *  filling missing fields with their defaults here, every consumer can
 *  assume a fully-shaped AppState regardless of the cache's age. */
export function loadCachedState(): AppState | null {
  try {
    const raw = localStorage.getItem(LS_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AppState>;
    const defaults: AppState = {
      roster: {},
      calendar: {},
      notes: {},
      meetings: {},
      currentUser: null,
      isAdmin: false,
      isSuperAdmin: false,
      actAs: null,
      profileId: null,
      adminEmail: "",
      profilesByRoster: {},
      closures: {},
    };
    // Shallow merge: top-level fields fall back to defaults when absent.
    // (Deep merge would risk dragging in stale nested data we'd rather
    // see overwritten by bootFromSupabase a moment later.)
    return { ...defaults, ...parsed };
  } catch {
    return null;
  }
}

export function clearCachedState(): void {
  try {
    localStorage.removeItem(LS_STATE_KEY);
  } catch {
    /* ignore */
  }
}
